import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { cn } from '@/lib/cn';
import { useThemeColors } from '@/lib/useThemeColors';

interface SectionAction {
  label: string;
  onPress: () => void;
}

interface SectionHeaderProps {
  /** Section title */
  title: string;
  /** Optional subtitle or count displayed below the title */
  subtitle?: string;
  /** Optional action button on the right side */
  action?: SectionAction;
  /** Optional right-side content (takes precedence over action) */
  right?: React.ReactNode;
  /** Additional className for the container */
  className?: string;
}

/**
 * Section header with title, optional subtitle, and optional action.
 * Extracted from the repeated pattern of title + action/count headers
 * used in "All Deals", "My Portfolio", "Portfolio Companies", etc.
 *
 * Usage:
 * ```tsx
 * <SectionHeader title="All Deals" subtitle="12 results" />
 * <SectionHeader
 *   title="My Portfolio"
 *   action={{ label: "Add", onPress: handleAdd }}
 * />
 * <SectionHeader title="Companies" right={<LayoutToggle />} />
 * ```
 */
export function SectionHeader({
  title,
  subtitle,
  action,
  right,
  className,
}: SectionHeaderProps) {
  const colors = useThemeColors();

  return (
    <View className={cn('flex-row items-center justify-between gap-3', className)}>
      {/* `flex-1 min-w-0` and single-line text: without them this block claims
          the full intrinsic width of its title and subtitle and pushes whatever
          is on the right off the screen. On the dealflow Pipeline header that
          right side is a toolbar — rejected-filter, mask toggle, three layout
          buttons and a count — and its trailing item was clipped at the screen
          edge in production (2026-08-09).

          Truncating the heading is the correct trade: a heading is readable at
          a glance from context, whereas a clipped control cannot be tapped. */}
      <View className="flex-1 min-w-0">
        <Text
          style={{ color: colors.text.primary }}
          className="font-semibold"
          accessibilityRole="header"
          numberOfLines={1}
        >
          {title}
        </Text>
        {subtitle && (
          <Text
            style={{ color: colors.text.tertiary }}
            className="text-sm"
            numberOfLines={1}
          >
            {subtitle}
          </Text>
        )}
      </View>
      {/* Controls do not shrink. Squeezing a toolbar produces unreadable icons
          and sub-44pt touch targets; letting the heading truncate instead keeps
          every control at full size and tappable. */}
      {right ? <View className="shrink-0">{right}</View> : (action && (
        <Pressable
          onPress={action.onPress}
          accessibilityRole="button"
          accessibilityLabel={action.label}
          className="px-3 py-1.5 rounded-lg shrink-0"
          style={{ backgroundColor: colors.primary[50] }}
        >
          <Text
            style={{ color: colors.primary[600] }}
            className="text-sm font-medium"
          >
            {action.label}
          </Text>
        </Pressable>
      ))}
    </View>
  );
}
